import React, { createContext, useContext, useRef } from 'react';
import { useSessionStorage } from 'react-use';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { Person } from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import CalibrationNudgeModal from 'talent_matching/components/modals/calibration_nudge_modal';

type AdvancedCandidateCountContextType = {
  onAdvanceSuccess: (application: Person) => void;
  onBulkAdvanceSuccess: (applications: Person[]) => void;
};

const AdvancedCandidateCountContext =
  createContext<AdvancedCandidateCountContextType>({
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onAdvanceSuccess: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBulkAdvanceSuccess: () => {},
  });

type Props = {
  openCalibrationModal: () => void;
  children: React.ReactNode;
};

export const AdvancedCandidateCountProvider = ({
  openCalibrationModal,
  children,
}: Props) => {
  const { userId, hiringPlanId, canCreateCalibrationSetting } =
    useUserContext();

  const [
    showCalibrationNudgeModal,
    openCalibrationNudgeModal,
    closeCalibrationNudgeModal,
  ] = useModal();

  const [advancedCandidateCount, setAdvancedCandidateCount] =
    useSessionStorage<number>(
      `advanced_candidate_count:${userId}:${hiringPlanId}`,
      0
    );

  // Keep a ref so concurrent callbacks always read the latest count without
  // relying on stale render-time closure values.
  const countRef = useRef(advancedCandidateCount);
  countRef.current = advancedCandidateCount;

  const isPartialOrLimitedMatch = (application: Person) =>
    application.matchStrength === 'partial_match' ||
    application.matchStrength === 'limited_match';

  const incrementCount = (by: number) => {
    if (countRef.current >= 5 || by <= 0) {
      return;
    }
    const increment = Math.min(by, 5 - countRef.current);
    countRef.current += increment;
    setAdvancedCandidateCount(countRef.current);
    if (countRef.current >= 5) {
      openCalibrationNudgeModal();
    }
  };

  const onAdvanceSuccess = (application: Person) => {
    if (isPartialOrLimitedMatch(application)) {
      incrementCount(1);
    }
  };

  const onBulkAdvanceSuccess = (applications: Person[]) => {
    const partialOrLimitedCount = applications.filter(
      isPartialOrLimitedMatch
    ).length;
    incrementCount(partialOrLimitedCount);
  };

  return (
    <AdvancedCandidateCountContext.Provider
      value={{ onAdvanceSuccess, onBulkAdvanceSuccess }}
    >
      {children}
      <CalibrationNudgeModal
        isOpen={showCalibrationNudgeModal}
        onClose={closeCalibrationNudgeModal}
        canCreateCalibrationSetting={canCreateCalibrationSetting}
        openCalibrationModal={openCalibrationModal}
      />
    </AdvancedCandidateCountContext.Provider>
  );
};

export const useAdvancedCandidateCount = () =>
  useContext(AdvancedCandidateCountContext);
